/**
 * Shared auth interceptor wiring for hey-api generated clients.
 *
 * Both the backend client (generatedClient.ts) and agents client
 * (generatedAgentsClient.ts) call attachAuthInterceptors() to get
 * identical Bearer-JWT + one-time 401 replay behaviour from shared state.
 */

import type { client as backendClient } from './generated/client.gen';
import { DEMO_JWT, isDemoMode } from '../lib/demoMode';
import {
  fetchWithSession,
  resetActiveRefresh,
  type SessionTokenSource,
} from '../auth/sessionFetch';
import { UnauthenticatedError } from '../auth/sessionExpiry';

type Client = typeof backendClient;

/**
 * The one session the transport authenticates with, set by the React auth
 * layer. A single slot rather than a separate token getter and refresher, so
 * one provider's token can never be paired with another provider's refresh.
 */
let sessionSource: SessionTokenSource | null = null;

/**
 * Supply (or clear) the session. `getB2BClient` in `auth/runtimeAuth.tsx`
 * registers each Stytch client's source when it creates the client (ENG-2687);
 * demo registers its static source below, at module scope.
 */
export function setSessionSource(source: SessionTokenSource | null) {
  if (source !== sessionSource) resetActiveRefresh();
  sessionSource = source;
}

// Demo mode: a static session token, the single non-Stytch token path.
// Registered at module scope, before any request can be issued — Playwright
// relies on demo headers never depending on effect timing. The token can never
// change, so the refresh fails instead of replaying the request with the same
// token: a demo 401 goes straight to `SessionExpiredError`.
if (isDemoMode()) {
  setSessionSource({
    getSessionJwt: () => DEMO_JWT,
    refreshSession: () =>
      Promise.reject(new Error('The demo session cannot be refreshed')),
  });
}

async function fetchWithRegisteredSession(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  // Capture the source once. A re-registration mid-request must not change
  // which session this request refreshes or replays with.
  const session = sessionSource;
  const nativeFetch = globalThis.fetch.bind(globalThis);

  // Public startup requests (before the auth layer registers a session, or on
  // a signed-out `/share/...` visit) go out with no credentials, so a 401 here
  // says nothing about any stored session. Fail closed: no refresh or replay.
  // (A source cleared after the request interceptor ran can leave a header on
  // this request; the 401 is still reported as unauthenticated.)
  if (!session) {
    const response = await nativeFetch(input, init);
    if (response.status === 401) throw new UnauthenticatedError();
    return response;
  }

  return fetchWithSession(input, init, session, nativeFetch);
}

/**
 * hey-api throws the parsed error body (no status field), which breaks
 * our 4xx-vs-5xx guards. Attach `status` off the real `Response` so consumers
 * can branch on it.
 *
 * Exported on its own so tests can have this half without the token plumbing —
 * `attachAuthInterceptors` also swaps `fetch` and turns a 401 into a thrown
 * `UnauthenticatedError`, which a unit test must not inherit.
 */
export function attachStatusInterceptor(client: Client) {
  client.interceptors.error.use(async (error, response) => {
    if (error && typeof error === 'object' && response) {
      (error as { status?: number }).status = (
        response as { status: number }
      ).status;
    }
    return error;
  });
}

/**
 * Register the request / error / response interceptors on the given hey-api
 * client. Reads the shared module-scoped session source, so both backend and
 * agents clients share the same token state.
 */
export function attachAuthInterceptors(client: Client) {
  // Replay belongs in the transport, not TanStack Query retry behavior.
  // This covers generated mutations (whose default retry count is zero),
  // plain SDK calls, queries, and SSE setup consistently.
  client.setConfig({ fetch: fetchWithRegisteredSession });

  client.interceptors.request.use((request) => {
    const token = sessionSource?.getSessionJwt();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  });

  attachStatusInterceptor(client);

  // Every 401 that gets this far went out with no credentials. `fetchWithSession`
  // owns the token-bearing ones end to end: it refreshes and replays once, and
  // a second 401 throws `SessionExpiredError` from in there — so a 401 it
  // *returns* is only ever the `!attemptedJwt` case. The no-session branch
  // above classifies and throws its own. Never decide this from the session
  // source, which is read long after the request was sent (ENG-1887).
  client.interceptors.response.use(async (response) => {
    if ((response as { status: number }).status !== 401) return response;
    throw new UnauthenticatedError();
  });
}
